import * as Operators from 'customer-data-filters-ui/filterQueryFormat/operator/Operators';
import { DATE, NUMBER, ENUMERATION } from 'customer-data-objects/property/PropertyTypes';
import { OrderedSet } from 'immutable';
import { useCallback, useContext } from 'react';
import { useStrategy } from './useStrategy';
import invariant from 'react-utils/invariant';
import getIn from 'transmute/getIn';
import SegmentationFiltersInstanceContext from '../state/SegmentationFiltersInstanceContext';
import { EVENT_BASED_FIELD_KEYS, getEventBasedFilterOperators, isConditionsPseudoGroup, isRefinableExtra } from '../strategy/eventBasedFilters';
import { Map as ImmutableMap } from 'immutable';
import { hasFieldOverrideV2 } from '../permissions/gates';
import { shouldUseEnumComponentsForExternalOptionsField } from '../utils/externalOptionsConversionUtils';
import { isRelativeTimeProperty } from '../utils/isRelativeTimeProperty';
import { isRichTextFieldType } from '../utils/isRichTextFieldType';
import { UNIFIED_EVENT } from '../filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { isAnyEventTypeId } from '../filterQueryFormat/ObjectTypeId';
export const useGetOperators = () => {
  const {
    getIsUngated
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getFieldDefinitions,
    getOperatorsForType,
    useHoursFiltering
  } = useStrategy();
  const getOperators = useCallback((filterFamily, field, condition) => {
    if (!field) {
      return OrderedSet();
    }
    const shouldUseEnumOperators = shouldUseEnumComponentsForExternalOptionsField(field, condition, filterFamily, getOperatorsForType, getIsUngated);
    if (shouldUseEnumOperators) {
      const enumOperators = getOperatorsForType(ENUMERATION, filterFamily, getIsUngated);
      return enumOperators || OrderedSet();
    }

    // some refinements on pseudo groups require custom operators sets
    // example is the refinement Page Location for Form Submission
    if (isConditionsPseudoGroup(filterFamily)) {
      const operatorsForEventBasedFilter = getEventBasedFilterOperators(field.get('name'));
      if (operatorsForEventBasedFilter) {
        return operatorsForEventBasedFilter;
      }
    }
    const fieldDefinitions = hasFieldOverrideV2(getIsUngated) ? ImmutableMap({}) : getFieldDefinitions(filterFamily);
    const operatorsByFieldDefinition = getIn([field.name, 'operators'], fieldDefinitions);
    if (operatorsByFieldDefinition) {
      return operatorsByFieldDefinition;
    }
    const referencedObjectTypeOperators = getOperatorsForType(field.referencedObjectType, filterFamily, getIsUngated, {
      useHoursFiltering
    });
    const fieldTypeOperators = getOperatorsForType(field.type, filterFamily, getIsUngated, {
      useHoursFiltering,
      isRelativeTimeProperty: isRelativeTimeProperty(field),
      isRichTextFieldType: field && isRichTextFieldType(field)
    });
    const operators = referencedObjectTypeOperators || fieldTypeOperators;
    invariant(operators, `No operator available for field ${field.name}`);
    return operators;
  }, [getIsUngated, getFieldDefinitions, getOperatorsForType, useHoursFiltering]);
  const getRefinementOperators = (filterFamily, field) => {
    if ((field === null || field === void 0 ? void 0 : field.name) === EVENT_BASED_FIELD_KEYS.EVENT_SUBJECT) {
      return OrderedSet([Operators.Equal]);
    }
    if ((field === null || field === void 0 ? void 0 : field.type) === DATE) {
      if (filterFamily === UNIFIED_EVENT || isAnyEventTypeId(filterFamily)) {
        return OrderedSet([Operators.LessOrEqual, Operators.GreaterOrEqual, Operators.InRange, Operators.NotInRange, Operators.LessThanRolling, Operators.GreaterThanRolling]);
      }
      const operators = OrderedSet([Operators.LessOrEqual, Operators.GreaterOrEqual, Operators.InRange]);
      if (filterFamily && isRefinableExtra(filterFamily)) {
        return operators.union(OrderedSet([Operators.LessThanRolling, Operators.GreaterThanRolling]));
      }
      return operators;
    }
    if ((field === null || field === void 0 ? void 0 : field.type) === NUMBER) {
      return OrderedSet([Operators.LessOrEqual, Operators.Equal, Operators.GreaterOrEqual]);
    }
    return OrderedSet([]);
  };
  return {
    getOperators,
    getRefinementOperators
  };
};